// quantity-bundle.js — versão com suporte a frequência (assinatura)

class QuantityBundle {
  constructor(container) {
    this.container = container;

    // Dados essenciais vindos do Liquid
    this.productId = container.dataset.productId;
    this.variantId = container.dataset.variantId;
    this.variantAvailable = container.dataset.variantAvailable === "true";

    // Frequência
    this.hasFrequency = container.dataset.hasFrequency === "true";
    this.selectedFrequency = container.dataset.defaultFrequency || "subscription";
    this.subscriptionExtraDiscount = parseFloat(container.dataset.subscriptionDiscount || 0);

    // Unidades por pack (1 unidade Shopify = N unidades físicas)
    this.unitsPerPack = parseInt(container.dataset.unitsPerPack) || 1;

    // Elementos importantes
    this.radioButtons = container.querySelectorAll(
      ".quantity-bundle__radio-input"
    );
    this.buyButton = container.querySelector(".quantity-bundle__buy-button");
    this.unitPriceEl = container.querySelector(".quantity-bundle__price-unit");
    this.comparePriceEl = container.querySelector(
      ".quantity-bundle__compare-price"
    );
    this.currentPriceEl = container.querySelector(
      ".quantity-bundle__current-price"
    );
    this.pixPriceEl = container.querySelector(".quantity-bundle__pix-price");
    this.parcelsPriceEl = container.querySelector(
      ".quantity-bundle__price-parcels"
    );
    this.tagDiscountEl = container.querySelector(
      ".quantity-bundle__discount-tag"
    );

    // Elementos de frequência
    this.freqRadios = container.querySelectorAll(".quantity-bundle__freq-radio");
    this.freqSavingsEl = container.querySelector(".quantity-bundle__freq-savings");
    this.totalAmountEl = container.querySelector(".quantity-bundle__total-amount");

    // Valores do Liquid
    this.parcelas = parseFloat(this.parcelsPriceEl.dataset.parcelas || 1);

    // Processar descontos do bundle
    const discountStr = container.dataset.discounts || "0";
    this.discounts = discountStr.split("/").map((v) => parseFloat(v));

    // Capturar quantidades e unidades físicas por opção
    const optionWraps = [...container.querySelectorAll(".quantity-bundle__option-wrap")];
    this.quantities = optionWraps.map((opt) => parseInt(opt.dataset.quantity));
    this.physicalUnitsPerOption = optionWraps.map((opt) => parseInt(opt.dataset.physicalUnits) || 0);

    // Preços manuais por opção (campo "preco" do metaobjeto), em centavos. 0 = usar cálculo automático.
    this.customComparePerOption = optionWraps.map((opt) => parseInt(opt.dataset.customCompare) || 0);
    this.customTotalPerOption = optionWraps.map((opt) => parseInt(opt.dataset.customTotal) || 0);

    // Preço base do produto (centavos → reais)
    this.basePrice = parseFloat(this.currentPriceEl.dataset.currentPrice) / 100;

    // Desconto PIX (%)
    this.pixDiscount = parseFloat(this.pixPriceEl.dataset.pixDiscount || 0);

    // Estado inicial
    const { value, position } = this.getSelectedQuantityAndPosition();
    this.selectedQuantity = value;
    this.position = position;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updatePrices();
    this.setupButton();
  }

  setupButton() {
    this.originalButtonText = this.buyButton.textContent;
  }

  bindEvents() {
    // Seleção de quantidade
    this.radioButtons.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.selectedQuantity = parseInt(radio.value);
        this.position = parseInt(radio.dataset.position);
        this.updatePrices();
        this.updateVisualSelection();
      });
    });

    this.container
      .querySelectorAll(".quantity-bundle__option")
      .forEach((option) => {
        option.addEventListener("click", (e) => {
          if (e.target.type !== "radio") {
            const radio = option.querySelector(".quantity-bundle__radio-input");
            if (radio) {
              radio.checked = true;
              radio.dispatchEvent(new Event("change"));
            }
          }
        });
      });

    // Seleção de frequência
    this.freqRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        this.selectedFrequency = radio.value;
        this.variantId = radio.dataset.variantId;
        this.variantAvailable = radio.dataset.variantAvailable !== 'false';
        const variantPrice = parseFloat(radio.dataset.variantPrice) / 100;
        this.basePrice = variantPrice;
        this.updateFrequencyVisuals();
        this.updatePrices();
        this.switchFlavorList(radio.value);
      });
    });

    this.buyButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.addToCart();
    });

    // Abertura do modal de benefícios da assinatura
    const modalTrigger = this.container.querySelector("[data-open-sub-modal]");
    if (modalTrigger) {
      modalTrigger.addEventListener("click", () => {
        window.dispatchEvent(new CustomEvent("open-sub-modal", {
          detail: { trigger: modalTrigger },
        }));
      });
    }

    // Sincronizar com mudança de variante do produto (pub/sub do Dawn)
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event.data.variant;
        if (variant && !this.hasFrequency) {
          this.variantId = variant.id;
          this.variantAvailable = variant.available;
          this.basePrice = variant.price / 100;
          this.updatePrices();
        }
      });
    }
  }

  updateFrequencyVisuals() {
    this.container
      .querySelectorAll(".quantity-bundle__freq-option")
      .forEach((opt) => opt.classList.remove("quantity-bundle__freq-option--active"));

    const checkedRadio = this.container.querySelector(
      ".quantity-bundle__freq-radio:checked"
    );
    if (checkedRadio) {
      checkedRadio.closest(".quantity-bundle__freq-option")
        .classList.add("quantity-bundle__freq-option--active");
    }
  }

  switchFlavorList(frequency) {
    const flavorsContainer = this.container.querySelector('.qb-v2__flavors');
    if (!flavorsContainer) return;

    const activeType = frequency === 'subscription' ? 'subscription' : 'onetime';

    flavorsContainer.dataset.activeList = activeType;
    flavorsContainer.querySelectorAll('.qb-v2__flavors-list').forEach(list => {
      list.style.display = list.dataset.list === activeType ? '' : 'none';
    });
  }

  getSelectedQuantityAndPosition() {
    const checked = this.container.querySelector(
      ".quantity-bundle__radio-input:checked"
    );
    const position = checked ? checked.dataset.position : 1;

    return checked
      ? {
          value: parseInt(checked.value),
          position: position,
        }
      : { value: this.quantities[0], position: 1 };
  }

  getFlavorsSubtotalCents() {
    const flavorsContainer = this.container.querySelector('.qb-v2__flavors');
    if (!flavorsContainer) return 0;

    const activeType = flavorsContainer.dataset.activeList || 'onetime';
    const activeList = flavorsContainer.querySelector('.qb-v2__flavors-list[data-list="' + activeType + '"]');
    if (!activeList) return 0;

    let sum = 0;
    activeList.querySelectorAll('.qb-v2__flavor').forEach((card) => {
      const qtyUnits = parseInt(card.querySelector('.qb-v2__flavor-qty').textContent) || 0;
      const price = parseInt(card.dataset.variantPrice) || 0;
      sum += price * qtyUnits;
    });
    return sum;
  }

  updatePrices() {
    const qtySelected = this.selectedQuantity;
    const positionSelected = this.position;
    const packQty = qtySelected;

    const index = positionSelected - 1;
    const discountPercentRaw = this.discounts[index] || 0;

    // Preço manual desta opção (campo "preco" do metaobjeto). 0 = usar cálculo automático.
    const hasCustomPrice = (this.customTotalPerOption[index] || 0) > 0;

    // Arredondar o desconto para inteiro (para exibição da tag)
    let discountPercent = Math.round(discountPercentRaw);

    // Unidades físicas (garrafas) do tier (do metaobjeto) vs. as realmente
    // selecionadas. Quando o usuário passa do maior tier, usamos as unidades
    // reais para escalar o preço pelo valor por unidade do tier.
    const tierQty = this.quantities[index] || qtySelected;
    const tierPhysicalUnits =
      this.physicalUnitsPerOption[index] ||
      (tierQty * this.unitsPerPack);
    // Quantas unidades físicas cada unidade de carrinho representa NESTE tier.
    // Derivado dos dados reais do tier (physical_units / quantidade) para evitar
    // inconsistências quando o `units_per_pack` global não bate com o metaobjeto
    // (ex.: quando a quantidade já é a contagem de unidades físicas).
    const perCartUnit = tierQty > 0 ? tierPhysicalUnits / tierQty : this.unitsPerPack;
    const actualPhysicalUnits = qtySelected * perCartUnit;
    // Dentro do tier usa as unidades do tier; em overflow usa as reais selecionadas.
    const physicalUnits = Math.max(actualPhysicalUnits, tierPhysicalUnits) || tierPhysicalUnits;

    // Calcular base em centavos
    let baseCents = Math.round(this.basePrice * 100);
    const flavorsSubtotalCents = this.getFlavorsSubtotalCents();
    if (flavorsSubtotalCents > 0 && packQty > 0) {
      baseCents = Math.round(flavorsSubtotalCents / packQty);
    }

    // Preço base por unidade física (garrafa). O Shopify arredonda o desconto
    // POR UNIDADE, então o desconto é calculado nesse nível para bater com o carrinho.
    const perUnitBaseCents = physicalUnits > 0 ? (baseCents * packQty) / physicalUnits : baseCents;

    // Desconto do bundle por unidade (floor por unidade, igual ao Shopify)
    const unitDiscountCents = Math.floor(perUnitBaseCents * discountPercentRaw / 100);
    const discountedUnitCents = perUnitBaseCents - unitDiscountCents;

    // Desconto combinado (bundle + assinatura) por unidade
    const combinedPct = discountPercentRaw + this.subscriptionExtraDiscount;
    const combinedDiscountUnitCents = Math.floor(perUnitBaseCents * combinedPct / 100);
    const subUnitCents = perUnitBaseCents - combinedDiscountUnitCents;

    // Totais = preço por unidade (com desconto) × unidades físicas
    let totalBundle = (discountedUnitCents * physicalUnits) / 100;
    let totalCompare = (perUnitBaseCents * physicalUnits) / 100;

    if (hasCustomPrice) {
      // Preço manual é fixo por tier. Quando o total selecionado excede o tier,
      // escala proporcionalmente pelo valor por unidade (scale = 1 dentro do tier).
      const customScale = tierPhysicalUnits > 0 ? physicalUnits / tierPhysicalUnits : 1;
      totalBundle = (this.customTotalPerOption[index] / 100) * customScale;
      totalCompare = ((this.customComparePerOption[index] || this.customTotalPerOption[index]) / 100) * customScale;
      discountPercent = totalCompare > totalBundle ? Math.round((totalCompare - totalBundle) / totalCompare * 100) : 0;
    }

    let totalCurrent = totalBundle;
    if (this.hasFrequency && this.selectedFrequency === "subscription") {
      totalCurrent = (subUnitCents * physicalUnits) / 100;
    }

    // Preço por unidade exibido (mantém o arredondamento de exibição por unidade)
    const discountMultiplier = (100 - discountPercentRaw) / 100;
    let priceUnitCents = perUnitBaseCents * discountMultiplier;
    if (this.hasFrequency && this.selectedFrequency === "subscription") {
      const subMultiplier = (100 - combinedPct) / 100;
      priceUnitCents = perUnitBaseCents * subMultiplier;
    }
    if (hasCustomPrice && physicalUnits > 0) {
      priceUnitCents = totalCurrent * 100 / physicalUnits;
    }
    const priceUnit = physicalUnits > 0 ? Math.floor(priceUnitCents) / 100 : 0;

    // Pix
    const pixDecimal = (100 - this.pixDiscount) / 100;
    const pixPrice = totalCurrent * pixDecimal;

    // Parcelado
    const parcelasValue = totalCurrent / this.parcelas;

    // Atualizar preços no DOM (pricing section, usado quando não tem frequência)
    if (this.unitPriceEl) this.unitPriceEl.textContent = `${this.formatPrice(priceUnit)} cada`;
    if (this.currentPriceEl) this.currentPriceEl.textContent = this.formatPrice(totalCurrent);
    if (this.parcelsPriceEl) this.parcelsPriceEl.textContent = `ou ${this.parcelas}x de ${this.formatPrice(parcelasValue)}`;
    if (this.pixPriceEl) {
      this.pixPriceEl.innerHTML = `${this.formatPrice(pixPrice)} ${this.pixPriceEl.textContent.split(" ").slice(-2).join(" ")}`;
    }

    // Aplicar valor arredondado na tag
    if (this.tagDiscountEl) this.tagDiscountEl.textContent = `${discountPercent}% OFF`;

    // Mostrar ou esconder elementos
    if (discountPercent <= 0) {
      if (this.comparePriceEl) this.comparePriceEl.style.display = "none";
      if (this.tagDiscountEl) this.tagDiscountEl.style.display = "none";
    } else {
      if (this.comparePriceEl) {
        this.comparePriceEl.style.display = "";
        this.comparePriceEl.textContent = this.formatPrice(totalCompare);
      }
      if (this.tagDiscountEl) this.tagDiscountEl.style.display = "";
    }

    // === Atualizar preços das cards de frequência ===
    if (this.hasFrequency) {
      const subTotal = (subUnitCents * physicalUnits) / 100;
      const subSavings = totalBundle - subTotal;
      const subCard = this.container.querySelector(
        ".quantity-bundle__freq-option--subscription .quantity-bundle__freq-price-value"
      );
      if (subCard) subCard.textContent = this.formatPrice(subTotal);

      // Atualizar savings
      if (this.freqSavingsEl) {
        this.freqSavingsEl.textContent = this.formatPrice(subSavings);
      }

      // Card de compra única: preço sem desconto extra
      const onetimeCard = this.container.querySelector(
        ".quantity-bundle__freq-option--onetime .quantity-bundle__freq-price-value"
      );
      if (onetimeCard) onetimeCard.textContent = this.formatPrice(totalBundle);

      // Atualizar sufixo da compra única com quantidade
      const onetimeSuffix = this.container.querySelector(
        ".quantity-bundle__freq-option--onetime .quantity-bundle__freq-price-suffix"
      );
      if (onetimeSuffix) onetimeSuffix.textContent = `/${physicalUnits} und`;

      // Atualizar sufixo da assinatura com quantidade
      const subSuffix = this.container.querySelector(
        ".quantity-bundle__freq-option--subscription .quantity-bundle__freq-price-suffix"
      );
      if (subSuffix) subSuffix.textContent = `/${physicalUnits} und`;

      // Atualizar preço por unidade da assinatura
      const subPerUnit = this.container.querySelector(
        ".quantity-bundle__freq-option--subscription .quantity-bundle__freq-price-per-unit"
      );
      if (subPerUnit && physicalUnits > 0) {
        const subMultiplier = (100 - combinedPct) / 100;
        const subPerUnitCents = perUnitBaseCents * subMultiplier;
        subPerUnit.textContent = `${this.formatPrice(Math.floor(subPerUnitCents) / 100)}/und`;
      }

      // Atualizar preço por unidade da compra única
      const onetimePerUnit = this.container.querySelector(
        ".quantity-bundle__freq-option--onetime .quantity-bundle__freq-price-per-unit"
      );
      if (onetimePerUnit && physicalUnits > 0) {
        const onetimeMultiplier = (100 - discountPercentRaw) / 100;
        const onetimePerUnitCents = hasCustomPrice
          ? totalBundle * 100 / physicalUnits
          : perUnitBaseCents * onetimeMultiplier;
        onetimePerUnit.textContent = `${this.formatPrice(Math.floor(onetimePerUnitCents) / 100)}/und`;
      }

      // Atualizar total line
      if (this.totalAmountEl) {
        this.totalAmountEl.textContent = this.formatPrice(totalCurrent);
      }
    }
  }

  updateVisualSelection() {
    this.container
      .querySelectorAll(".quantity-bundle__option")
      .forEach((opt) =>
        opt.classList.remove("quantity-bundle__option--selected")
      );

    const selected = this.container.querySelector(
      ".quantity-bundle__radio-input:checked"
    );
    if (selected) {
      selected
        .closest(".quantity-bundle__option")
        .classList.add("quantity-bundle__option--selected");
    }
  }

  formatPrice(price) {
    return "R$ " + price.toFixed(2).replace(".", ",");
  }

  async addToCart() {
    if (this.buyButton.disabled) return;

    try {
      this.buyButton.disabled = true;
      this.showLoadingState();

      const flavorsContainer = this.container.querySelector('.qb-v2__flavors');
      let body;

      // Desconto do bundle selecionado
      const bundleDiscount = this.discounts[this.position - 1] || 0;

      if (flavorsContainer) {
        const activeType = flavorsContainer.dataset.activeList || 'onetime';
        const activeList = flavorsContainer.querySelector('.qb-v2__flavors-list[data-list="' + activeType + '"]');
        const flavorCards = activeList ? activeList.querySelectorAll('.qb-v2__flavor') : [];

        if (flavorCards.length > 0) {
          const items = [];
          const unavailable = [];
          let totalQty = 0;

          flavorCards.forEach(card => {
            const qty = parseInt(card.querySelector('.qb-v2__flavor-qty').textContent) || 0;
            const variantId = card.dataset.variantId;
            const isAvailable = card.dataset.available !== 'false';
            const flavorName = card.dataset.flavorName || 'Produto';
            totalQty += qty;

            if (qty > 0) {
              if (!isAvailable) {
                unavailable.push(flavorName);
              } else if (variantId) {
                const item = { id: parseInt(variantId), quantity: qty };
                if (bundleDiscount > 0) {
                  item.properties = { _bundle_discount: bundleDiscount };
                }
                items.push(item);
              }
            }
          });

          if (unavailable.length > 0) {
            throw new Error(unavailable.join(', ') + (unavailable.length === 1 ? ' está esgotado' : ' estão esgotados'));
          }

          if (items.length === 0 || totalQty === 0) {
            this.showErrorFeedback();
            return;
          }
          body = JSON.stringify({ items });
        }
      }

      if (!body) {
        if (!this.variantAvailable) {
          throw new Error('Produto esgotado');
        }
        const cartQty = this.selectedQuantity;
        const cartData = {
          id: this.variantId,
          quantity: cartQty,
        };
        if (bundleDiscount > 0) {
          cartData.properties = { _bundle_discount: bundleDiscount };
        }
        body = JSON.stringify(cartData);
      }

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || errorData.message || "Erro ao adicionar ao carrinho");
      }

      this.showSuccessFeedback();
      setTimeout(() => this.openCartDrawer(), 1200);
    } catch (err) {
      console.error(err);
      this.showErrorFeedback(err.message);
    }
  }

  showLoadingState() {
    this.buyButton.innerHTML =
      '<span class="quantity-bundle__spinner"></span>';
  }

  showSuccessFeedback() {
    this.buyButton.innerHTML =
      '<svg class="quantity-bundle__check-anim" width="28" height="28" viewBox="0 0 28 28" fill="none">' +
        '<circle class="quantity-bundle__check-circle" cx="14" cy="14" r="12" stroke="#fff" stroke-width="2"/>' +
        '<path class="quantity-bundle__check-path" d="M8.5 14.5L12 18L19.5 10.5" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>' +
      '<span class="quantity-bundle__check-text">Adicionado!</span>';
    setTimeout(() => this.restoreButton(), 2200);
  }

  showErrorFeedback(message) {
    this.buyButton.innerHTML = message || "Erro - tente novamente";
    setTimeout(() => this.restoreButton(), 3500);
  }

  restoreButton() {
    this.buyButton.disabled = false;
    this.buyButton.innerHTML = this.originalButtonText;
  }

  async openCartDrawer() {
    const response = await fetch("/?sections=cart-drawer,cart-icon-bubble");
    const sections = await response.json();
    const parsedState = { sections };

    const drawer = document.querySelector("cart-drawer");
    if (!drawer) return;

    drawer.classList.remove("is-empty");
    drawer.renderContents(parsedState);

    requestAnimationFrame(() => {
      drawer.classList.add("animate", "active");
      document.body.classList.add("overflow-hidden");
    });
  }
}

// AUTO INIT
function initQuantityBundles() {
  document.querySelectorAll(".quantity-bundle").forEach((bundle) => {
    bundle._qbInstance = new QuantityBundle(bundle);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initQuantityBundles);
} else {
  initQuantityBundles();
}

document.addEventListener("shopify:section:load", (e) => {
  e.target
    .querySelectorAll(".quantity-bundle")
    .forEach((bundle) => { bundle._qbInstance = new QuantityBundle(bundle); });
});
